/**
 * Environment & platform vocabulary and per-step variant model (Workshop W8,
 * WORKSHOP-FOUNDATION.md §6). The same procedure often differs by OS, shell,
 * and target environment; rather than duplicating a whole Recipe (which drifts),
 * a step carries a shared base plus context-specific *alternates*, and the
 * reader *states* their context — Rulebound never sniffs the environment
 * (principle 10). Matching is app-side; the vocabulary lives in code (not a
 * Postgres enum) because W8 churns it and enum migrations are the expensive
 * kind of churn (§1). If variants ever need to be queryable, promote to a table.
 */

export const OS_VALUES = ["windows", "macos", "linux"] as const;
export const SHELL_VALUES = ["bash", "zsh", "powershell", "cmd", "sql", "other"] as const;
export const TARGET_VALUES = ["local", "dev", "test", "staging", "prod"] as const;

export type OSValue = (typeof OS_VALUES)[number];
export type ShellValue = (typeof SHELL_VALUES)[number];
export type TargetValue = (typeof TARGET_VALUES)[number];

export const OS_LABELS: Record<OSValue, string> = {
  windows: "Windows",
  macos: "macOS",
  linux: "Linux",
};
export const SHELL_LABELS: Record<ShellValue, string> = {
  bash: "Bash",
  zsh: "Zsh",
  powershell: "PowerShell",
  cmd: "cmd",
  sql: "SQL",
  other: "Other",
};
export const TARGET_LABELS: Record<TargetValue, string> = {
  local: "Local",
  dev: "Dev",
  test: "Test",
  staging: "Staging",
  prod: "Prod",
};

/** A subset of contexts something applies to. An absent/empty dimension is
 * unconstrained ("any"); an empty selector matches every stated context. */
export type EnvSelector = {
  os?: OSValue[];
  shell?: ShellValue[];
  target?: TargetValue[];
};

/** The single context a reader has stated they're working in. */
export type ReaderContext = {
  os?: OSValue;
  shell?: ShellValue;
  target?: TargetValue;
};

/** One context-specific rewrite of a step's body. */
export type StepAlternate = {
  id: string; // stable local id, so edits/reorders are addressable
  match: EnvSelector; // empty => the fallback (matches any stated context)
  body: string; // replaces the base body when this alternate is active
  note?: string; // optional short author label, e.g. "PowerShell"
};

/** The shape stored in `recipe_steps.variants` (jsonb). */
export type StepVariants = {
  /** Conditional step: if set, the step only applies in matching contexts. */
  appliesTo?: EnvSelector;
  /** Ordered body rewrites; first match wins, so put a fallback last. */
  alternates?: StepAlternate[];
};

export const CONTEXT_STORAGE_KEY = "rb:reader-env";
export const CONTEXT_EVENT = "rb:reader-env";

export function isContextStated(ctx: ReaderContext): boolean {
  return !!(ctx.os || ctx.shell || ctx.target);
}

export function selectorIsEmpty(sel: EnvSelector | null | undefined): boolean {
  return !sel || (!sel.os?.length && !sel.shell?.length && !sel.target?.length);
}

/**
 * Does a selector match the reader's stated context? Each dimension the
 * selector constrains requires the reader to have stated a value that's in the
 * allowed set; unconstrained dimensions are ignored. An empty selector always
 * matches — that's how a fallback alternate is expressed.
 */
export function selectorMatches(sel: EnvSelector | null | undefined, ctx: ReaderContext): boolean {
  if (!sel) return true;
  if (sel.os?.length && (!ctx.os || !sel.os.includes(ctx.os))) return false;
  if (sel.shell?.length && (!ctx.shell || !sel.shell.includes(ctx.shell))) return false;
  if (sel.target?.length && (!ctx.target || !sel.target.includes(ctx.target))) return false;
  return true;
}

export type StepResolution = {
  /** false => the step is out of scope for this context (conditional step). */
  applies: boolean;
  /** The body to show — the base, or an active alternate's rewrite. */
  body: string | null;
  activeAlternate: StepAlternate | null;
  /** Context stated + alternates exist, but none matched and there's no
   * fallback — the reader is warned rather than silently shown the base. */
  unmatched: boolean;
};

/** Resolve how a step should read for a given reader context. */
export function resolveStep(
  variants: StepVariants | null,
  baseBody: string | null,
  ctx: ReaderContext,
): StepResolution {
  const stated = isContextStated(ctx);
  const applies = !stated || selectorIsEmpty(variants?.appliesTo) || selectorMatches(variants!.appliesTo, ctx);
  const alts = variants?.alternates ?? [];

  if (!alts.length || !stated) {
    return { applies, body: baseBody, activeAlternate: null, unmatched: false };
  }
  const match = alts.find((a) => selectorMatches(a.match, ctx)) ?? null;
  if (match) return { applies, body: match.body, activeAlternate: match, unmatched: false };
  return { applies, body: baseBody, activeAlternate: null, unmatched: true };
}

/** Whether a recipe uses any variant plumbing (so the reader shows a context
 * picker only where it matters). */
export function recipeHasVariants(variantsList: (StepVariants | null)[]): boolean {
  return variantsList.some((v) => !selectorIsEmpty(v?.appliesTo) || (v?.alternates?.length ?? 0) > 0);
}

/** Human phrase for a stated context: "Windows · PowerShell · staging". */
export function describeContext(ctx: ReaderContext): string {
  const parts: string[] = [];
  if (ctx.os) parts.push(OS_LABELS[ctx.os]);
  if (ctx.shell) parts.push(SHELL_LABELS[ctx.shell]);
  if (ctx.target) parts.push(TARGET_LABELS[ctx.target]);
  return parts.join(" · ");
}

/** Human phrase for a selector: "Windows/Linux · Bash", or "Any environment". */
export function describeSelector(sel: EnvSelector | null | undefined): string {
  if (selectorIsEmpty(sel)) return "Any environment";
  const parts: string[] = [];
  if (sel!.os?.length) parts.push(sel!.os.map((o) => OS_LABELS[o]).join("/"));
  if (sel!.shell?.length) parts.push(sel!.shell.map((s) => SHELL_LABELS[s]).join("/"));
  if (sel!.target?.length) parts.push(sel!.target.map((t) => TARGET_LABELS[t]).join("/"));
  return parts.join(" · ");
}

function parseStringArray<T extends string>(raw: unknown, allowed: readonly T[]): T[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const set = new Set<string>(allowed);
  const out = raw.filter((v): v is T => typeof v === "string" && set.has(v));
  return out.length ? out : undefined;
}

function parseSelector(raw: unknown): EnvSelector | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const obj = raw as Record<string, unknown>;
  const sel: EnvSelector = {};
  const os = parseStringArray(obj.os, OS_VALUES);
  const shell = parseStringArray(obj.shell, SHELL_VALUES);
  const target = parseStringArray(obj.target, TARGET_VALUES);
  if (os) sel.os = os;
  if (shell) sel.shell = shell;
  if (target) sel.target = target;
  return selectorIsEmpty(sel) ? undefined : sel;
}

function parseAlternate(raw: unknown): StepAlternate | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const obj = raw as Record<string, unknown>;
  if (typeof obj.id !== "string" || typeof obj.body !== "string") return null;
  const alt: StepAlternate = {
    id: obj.id,
    body: obj.body,
    match: parseSelector(obj.match) ?? {},
  };
  if (typeof obj.note === "string" && obj.note.trim()) alt.note = obj.note;
  return alt;
}

/** Parse the jsonb `variants` column defensively; null when there's nothing. */
export function parseStepVariants(raw: unknown): StepVariants | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const obj = raw as Record<string, unknown>;
  const out: StepVariants = {};
  const appliesTo = parseSelector(obj.appliesTo);
  if (appliesTo) out.appliesTo = appliesTo;
  if (Array.isArray(obj.alternates)) {
    const alts = obj.alternates
      .map(parseAlternate)
      .filter((a): a is StepAlternate => a !== null);
    if (alts.length) out.alternates = alts;
  }
  return out.appliesTo || out.alternates ? out : null;
}
